import { memo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ListFilterCard } from '../list-kit/ListFilterCard.js';
import { FILTER_SHEET_HINT, FILTER_SHEET_TITLE } from './filterSheetFields.js';
import { Button } from './surfacePrimitives.js';

export interface FilterSheetShellProps {
  readonly open: boolean;
  readonly children: ReactNode;
  readonly onApply: () => void;
  /** Reset draft + applied filters (Spec Reset — not Vymazat). */
  readonly onReset: () => void;
  /** Extra footer actions (e.g. Správa kategorií) after Apply/Reset. */
  readonly footerExtra?: ReactNode;
  readonly title?: string;
  readonly applyLabel?: string;
  readonly resetLabel?: string;
  readonly className?: string;
  readonly testId?: string;
  readonly hint?: ReactNode;
}

/** Root width: full content track so filter fields can flow horizontally. */
const FILTER_SHEET_ROOT_CLASS = 'w-full min-w-0 max-w-full';

/**
 * FA-05 owner — Spec FilterDrawer **content shell** only (Filtry + body + Použít / Reset).
 * Parent feature code owns open/close, backdrop, and BN/safe-area overlay geometry.
 * Width is owned here (not per feature) so every Filtry sheet spans the list track.
 */
export const FilterSheetShell = memo<FilterSheetShellProps>(
  ({
    open,
    children,
    onApply,
    onReset,
    footerExtra,
    title,
    applyLabel,
    resetLabel,
    className,
    testId = 'filter-sheet-shell',
    hint,
  }): JSX.Element | null => {
    const { t } = useTranslation('admin');

    if (!open) {
      return null;
    }

    const resolvedTitle = title ?? t('shared.filterSheet.title');
    const resolvedApply = applyLabel ?? t('shared.filterSheet.apply');
    const resolvedReset = resetLabel ?? t('shared.filterSheet.reset');

    return (
      <div data-export-exclude="chrome" className={FILTER_SHEET_ROOT_CLASS}>
        <ListFilterCard
          className={[
            'w-full max-w-full flex-col items-stretch gap-[var(--space-3)] px-[var(--space-3)] py-[var(--space-3)]',
            className,
          ]
            .filter(Boolean)
            .join(' ')}
        >
          <div
            className="flex w-full flex-col gap-[var(--space-3)]"
            data-testid={testId}
            role="region"
            aria-label={resolvedTitle}
          >
            <div className="flex w-full min-w-0 flex-col gap-1">
              <h3 className={FILTER_SHEET_TITLE}>{resolvedTitle}</h3>
              {typeof hint === 'string' ? <p className={FILTER_SHEET_HINT}>{hint}</p> : hint}
            </div>
            {children}
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                intent="primary"
                size="sm"
                onClick={onApply}
                data-testid={`${testId}-apply`}
              >
                {resolvedApply}
              </Button>
              <Button
                type="button"
                intent="secondary"
                size="sm"
                onClick={onReset}
                data-testid={`${testId}-reset`}
              >
                {resolvedReset}
              </Button>
              {footerExtra}
            </div>
          </div>
        </ListFilterCard>
      </div>
    );
  },
);

FilterSheetShell.displayName = 'FilterSheetShell';
